import {
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationResult,
  UseQueryResult,
} from "@tanstack/react-query"
import { AxiosResponse } from "axios"

import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import { useApiCommand } from "hooks/core/useApiCommand"
import { queryKeys } from "api/queryKeys"
import {
  compareTrackerMetadata,
  addToTracker,
  getTrackerProjects,
  removeFromTracker,
} from "api/Repos/trackers"
import { notify } from "shared/ui/Notifications/notify"
import { failureOf } from "utils/errMsgOf"
import {
  TrackerAddResponse,
  TrackerCompareResponse,
  TrackerProjectsResult,
} from "types/questionnaire.types"
import { ProjectRef, useProjectOwner } from "./useProjectOwner"

/**
 * The projects in the open project's tracker (its waves). Silent: the Tracker section says
 * inline when they couldn't be read.
 */
export const useTrackerQuery = (
  projectId: string
): UseQueryResult<TrackerProjectsResult> => {
  const getAxios = useAuthedAxios()
  const owner = useProjectOwner()
  return useQuery({
    queryKey: queryKeys.provenance.tracker(projectId, owner),
    enabled: !!projectId && !!owner,
    meta: { silent: true },
    queryFn: async (): Promise<TrackerProjectsResult> =>
      (await getTrackerProjects(await getAxios(), projectId, owner)).data,
  })
}

/** A wave joining or leaving the tracker of the open project (`projectId`). */
interface TrackerChange {
  wave: ProjectRef
  projectId: string
}

/**
 * Reads again what a tracker change touched: both projects' histories (the change is recorded
 * there) and their tracker lists. Runs whether the change worked or not, so the list shows what
 * the server holds.
 */
const useInvalidateTrackerChange = () => {
  const queryClient = useQueryClient()
  return ({ wave, projectId }: TrackerChange) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.provenance.project(projectId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.provenance.project(wave.id) }),
    ])
}

/** Adds another project (`wave`) to the open project's tracker. */
export const useAddToTracker = (): UseMutationResult<
  TrackerAddResponse,
  Error,
  TrackerChange
> => {
  const getAxios = useAuthedAxios()
  const owner = useProjectOwner()
  const invalidate = useInvalidateTrackerChange()
  return useMutation({
    mutationFn: async ({ wave, projectId }: TrackerChange) =>
      addToTracker(await getAxios(), wave.id, wave.owner, projectId, owner),
    onError: (error) => notify.error("Error Adding Wave!", failureOf(error)),
    onSettled: (_res, _error, change) => invalidate(change),
  })
}

/** Takes a project (`wave`, which may be the open project itself) out of its tracker. */
export const useRemoveFromTracker = (): UseMutationResult<
  AxiosResponse<{ ok: boolean }>,
  Error,
  TrackerChange
> => {
  const getAxios = useAuthedAxios()
  const invalidate = useInvalidateTrackerChange()
  return useMutation({
    mutationFn: async ({ wave }: TrackerChange) =>
      removeFromTracker(await getAxios(), wave.id, wave.owner),
    onError: (error) => notify.error("Error Removing Wave!", failureOf(error)),
    onSettled: (_res, _error, change) => invalidate(change),
  })
}

/**
 * Compares two tracker members' questionnaires, for the tracker report (a one-off export over
 * every pair of waves, so a command rather than a cached read).
 */
export const useTrackers = () => {
  const [compareById] = useApiCommand<
    [string, string, string, string],
    TrackerCompareResponse
  >(compareTrackerMetadata)
  return { compareById }
}
